import { useState, useRef, MutableRefObject } from 'react'
import type { HotTable } from '@handsontable/react'
import { t } from '@lingui/core/macro'
import { CommentsSelect, SortBy } from '@admin/template-elements/edit-list/comments-select'
import { type ModalInterface } from '@components'
import { Button } from '@components/v2/button'
import { Comment } from './comment'
import { FeedbackButton } from './feedback-button'
import { FeedbackModal } from './feedback-modal'
import { SidePanel, SidePanelInterface } from './side-panel'
import { getListValueId } from '../helpers'
import { GeneralCommentDialog } from '../general-comment-dialog'
import type { List, QueuedResolutionStatus } from '../types'
import { ThreadData } from '@liveblocks/client'
import Handsontable from 'handsontable'

interface SuggestionsSidePanelProps {
  list: List
  sidePanelRef: React.RefObject<SidePanelInterface>
  onToggle: (isOpen: boolean) => void
  selectedCommentType: SortBy | undefined
  setSelectedCommentType: (value: SortBy) => void
  threadsToDisplayInSidePanel: ThreadData[]
  hasUnresolvedGeneralComments: boolean
  isApproverMode: boolean
  isLocked: boolean
  tableRef: MutableRefObject<HotTable | null>
  hotInstance?: Handsontable | null
  updateLocalThreadStatus: (threadId: string, status: QueuedResolutionStatus) => void
  queueThreadResolution: (thread: ThreadData, status: QueuedResolutionStatus) => void
}

export const SuggestionsSidePanel = ({
  list,
  sidePanelRef,
  onToggle,
  selectedCommentType,
  setSelectedCommentType,
  threadsToDisplayInSidePanel,
  hasUnresolvedGeneralComments,
  isApproverMode,
  isLocked,
  tableRef,
  hotInstance,
  updateLocalThreadStatus,
  queueThreadResolution,
}: SuggestionsSidePanelProps) => {
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  const feedbackModalRef = useRef<ModalInterface>(null)
  const [isGeneralCommentVisible, setIsGeneralCommentVisible] = useState(false)

  const selectedCommentTypeLabel = selectedCommentType?.toLowerCase() ?? ''

  const handleFeedbackModalOpen = () => {
    feedbackModalRef.current?.handleOpenModal()
  }

  const handleGeneralCommentScroll = () => {
    // Scroll to bottom when opening the dialog
    setTimeout(() => {
      scrollableContentRef.current?.scrollTo({
        top: scrollableContentRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 100)
  }

  return (
    <>
      <SidePanel ref={sidePanelRef} getToggleStatus={onToggle}>
        <div className="pt-6 pr-4 pl-7 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center mb-8">
            <span style={{ fontSize: 24, fontWeight: 600 }}>{t`Comments`}</span>
            <div className="flex gap-[16px]">
              <FeedbackButton onClick={handleFeedbackModalOpen} />
              <CommentsSelect value={selectedCommentType} onChange={setSelectedCommentType} />
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col pt-10" ref={scrollableContentRef}>
          <div className="flex pb-4 pr-4 pl-7 gap-4 flex-col">
            {threadsToDisplayInSidePanel.map(thread => {
              const coords = hotInstance
                ?.getData()
                .flatMap((row: unknown[], rIdx) =>
                  row.map((_, cIdx) => {
                    const val = getListValueId(list, rIdx, cIdx)
                    return val === thread.metadata.listValueId ? { r: rIdx + 1, c: cIdx + 1 } : null
                  }),
                )
                .find(Boolean)

              return (
                <Comment
                  key={thread.id}
                  thread={{ ...thread, ...(coords && { table: { coords } }) }}
                  updateLocalThreadStatus={updateLocalThreadStatus}
                  queueThreadResolution={queueThreadResolution}
                  tableRef={tableRef}
                  list={list}
                  isApproverMode={isApproverMode}
                  inReviewMode={isLocked}
                />
              )
            })}
          </div>
          {!threadsToDisplayInSidePanel.length && selectedCommentType && (
            <span className="pr-4 pl-7">
              {selectedCommentType === SortBy.ALL_TYPES
                ? t`No comments`
                : t`No ${selectedCommentTypeLabel} comments`}
            </span>
          )}
          <GeneralCommentDialog onVisibilityChange={setIsGeneralCommentVisible} />
        </div>
        <div className="py-6 pr-4 pl-7 border-t bg-white">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Button
              variant="secondary"
              onClick={handleGeneralCommentScroll}
              disabled={isGeneralCommentVisible}
            >
              {t`Add Comment`}
            </Button>
            {/* red icon to notify user of ongoing general comments or threads */}
            {hasUnresolvedGeneralComments && (
              <div
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  border: '2px solid white',
                }}
              />
            )}
          </div>
        </div>
      </SidePanel>
      <FeedbackModal ref={feedbackModalRef} />
    </>
  )
}
